/**
 * Hermes Badge - Status badge with module colors
 *
 * Features:
 * - Module color variants with semantic states
 * - Animated appearance (fade + scale)
 * - Pill shape with proper padding
 * - TypeScript enum for badge types
 * - Accessibility support
 */

import React from 'react';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';

export type BadgeVariant =
  | 'courier'
  | 'santa'
  | 'email'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'neutral';

export type BadgeSize = 'small' | 'medium' | 'large';

export interface HermesBadgeProps {
  /**
   * Badge color variant
   * @default 'primary'
   */
  variant?: BadgeVariant;

  /**
   * Badge size
   * @default 'medium'
   */
  size?: BadgeSize;

  /**
   * Badge content (text or number)
   */
  children: React.ReactNode;

  /**
   * Optional icon to display before text
   */
  icon?: React.ReactNode;

  /**
   * Whether badge should be outlined instead of filled
   * @default false
   */
  outlined?: boolean;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Click handler
   */
  onClick?: () => void;
}

// Size configurations
const sizeConfig: Record<BadgeSize, { padding: string; fontSize: string; height: string; iconSize: number }> = {
  small: {
    padding: '2px 8px',
    fontSize: '0.6875rem',
    height: '20px',
    iconSize: 12,
  },
  medium: {
    padding: '4px 12px',
    fontSize: '0.75rem',
    height: '24px',
    iconSize: 14,
  },
  large: {
    padding: '6px 16px',
    fontSize: '0.875rem',
    height: '32px',
    iconSize: 16,
  },
};

// Styled badge container
const StyledBadge = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'variant' && prop !== 'size' && prop !== 'outlined' && prop !== 'clickable',
})<{
  variant: BadgeVariant;
  size: BadgeSize;
  outlined: boolean;
  clickable: boolean;
}>(({ theme, variant, size, outlined, clickable }) => {
  // Get color from theme palette
  const getColor = () => {
    const paletteKey = variant as keyof typeof theme.palette;
    const color = theme.palette[paletteKey] as any;

    if (variant === 'neutral') {
      return {
        main: theme.palette.grey[600],
        light: theme.palette.grey[100],
        contrastText: theme.palette.common.white,
      };
    }

    return color || theme.palette.primary;
  };

  const color = getColor();
  const config = sizeConfig[size];

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    padding: config.padding,
    height: config.height,
    borderRadius: 12,
    fontSize: config.fontSize,
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontWeight: 600,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    transition: `all ${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeInOut}`,

    // Filled style
    ...(!outlined && {
      backgroundColor: color.main,
      color: color.contrastText || theme.palette.common.white,
    }),

    // Outlined style
    ...(outlined && {
      backgroundColor: 'transparent',
      color: color.main,
      border: `2px solid ${color.main}`,
    }),

    // Clickable interaction
    ...(clickable && {
      cursor: 'pointer',
      userSelect: 'none',

      '&:hover': {
        backgroundColor: outlined ? `${color.main}15` : color.dark || color.main,
        transform: 'translateY(-1px)',
        boxShadow: `0 2px 8px ${color.main}40`,
      },

      '&:active': {
        transform: 'translateY(0)',
      },
    }),
  };
});

// Icon wrapper
const IconWrapper = styled('span')<{ size: BadgeSize }>(({ size }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: sizeConfig[size].iconSize,
  height: sizeConfig[size].iconSize,

  '& > svg': {
    width: '100%',
    height: '100%',
  },
}));

// Animation variants
const badgeVariants = {
  initial: {
    opacity: 0,
    scale: 0.8,
  },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      type: 'spring' as const,
      stiffness: 500,
      damping: 25,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    transition: {
      duration: 0.15,
    },
  },
  hover: {
    scale: 1.05,
    transition: {
      type: 'spring' as const,
      stiffness: 400,
      damping: 17,
    },
  },
  tap: {
    scale: 0.95,
  },
};

const noMotionVariants = {
  initial: {},
  animate: {},
  exit: {},
  hover: {},
  tap: {},
};

export const Badge: React.FC<HermesBadgeProps> = ({
  variant = 'primary',
  size = 'medium',
  children,
  icon,
  outlined = false,
  className,
  onClick,
}) => {
  const clickable = onClick !== undefined;

  // Check for reduced motion preference
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const variants = prefersReducedMotion ? noMotionVariants : badgeVariants;

  return (
    <motion.div
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
      whileHover={clickable ? 'hover' : undefined}
      whileTap={clickable ? 'tap' : undefined}
      style={{ display: 'inline-block' }}
    >
      <StyledBadge
        variant={variant}
        size={size}
        outlined={outlined}
        clickable={clickable}
        className={className}
        onClick={onClick}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onKeyDown={
          clickable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick?.();
                }
              }
            : undefined
        }
      >
        {icon && <IconWrapper size={size}>{icon}</IconWrapper>}
        {children}
      </StyledBadge>
    </motion.div>
  );
};

Badge.displayName = 'HermesBadge';

export default Badge;
